import type { MatchTeam } from '@/lib/factions'

export interface MatchSignUp {
  user_id: string
  name: string
  class_name: string
  class_emote_id?: string | null
  spec_name?: string | null
  spec_emote_id?: string | null
  is_active: boolean
  is_bench?: boolean
  avatar_url?: string | null
}

export interface MatchEvent {
  id: string
  title: string
  description?: string
  start_time?: number
  briefing_time?: number | null
  /** Hidden from members and never announced on Discord. */
  staff_only?: boolean
  active_count: number
  sign_ups: MatchSignUp[]
  is_external?: boolean
  discord_url?: string
  map_name?: string
  teams?: MatchTeam[]
}

/** Where a dashboard match stands in its three steps, for the viewer. */
export interface MatchProgress {
  roster_posted: boolean
  /** The viewer's place in the published roster; role `substitute` = bench. */
  position: { squad: string; role: string; status: 'pending' | 'yes' | 'no' } | null
  strategy: { id: number; title: string } | null
}

export interface Match {
  event_id: string
  event: MatchEvent
  manual?: boolean
  /** Created on the dashboard (as opposed to read from Raid Helper). */
  local?: boolean
  progress?: MatchProgress
}

export type StepKey = 'signup' | 'position' | 'strategy'
export type StepState = 'done' | 'action' | 'waiting'

/**
 * Each step as the viewer stands in it. `action` means the step waits on them
 * — the one state drawn in crimson, in the list and on the match page alike.
 */
export function stepStates(match: Match, userId: string | undefined, now: number): Record<StepKey, StepState> {
  const upcoming = !match.event.start_time || match.event.start_time > now
  const signed = !!userId && match.event.sign_ups.some(s => s.user_id === userId)
  const p = match.progress
  const pos = p?.position
  return {
    signup: signed ? 'done' : upcoming ? 'action' : 'waiting',
    position: !p?.roster_posted ? 'waiting'
      // Only a placed player has a slot to confirm; the bench has nothing to answer.
      : pos && pos.role !== 'substitute' && pos.status === 'pending' && upcoming ? 'action'
      : 'done',
    strategy: p?.strategy ? 'done' : 'waiting',
  }
}
